// Role detail page wrapper.
//
// CHG-1663 / ITER-3772 (Wave 1 / Tier 1 — Roles port).
//
// Thin wrapper around the generator-emitted roleDetailConfig from
// specs/tier-05-experience/role.entity-hint.yaml. The generated
// config carries idParam / queryFn / title / statusCards; this
// wrapper layers on:
//   - stateful actions (Edit + Edit-permissions + Delete) — modals
//     need component state that can't live in a static config const
//   - a Permissions tab that mounts the hand-rolled PermissionMatrix
//     (the generator emits the empty custom-tabs override-block; the
//     wrapper supplies the actual tab.render callback rather than
//     hand-editing the generated file)
//   - a small Overview tab with description / type / slug detail
//     since the generator's statusCards already covers the metric row

import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DetailPage, type DetailPageConfig } from "../components/composition";
import { roleDetailConfig } from "../generated/screens/role-detail";
import { rolesApi, type Role } from "../api/roles";
import { RoleFormModal } from "../components/role/RoleFormModal";
import { PermissionMatrix } from "../components/role/PermissionMatrix";

type ModalState = { kind: "edit"; role: Role } | { kind: "permissions"; role: Role } | null;

function InfoRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 12, color: "var(--color-text-muted)" }}>
        {label}
      </span>
      <span style={{ fontSize: 14, color: "var(--color-text)" }}>{value ?? "—"}</span>
    </div>
  );
}

export function RoleDetail() {
  const [modal, setModal] = useState<ModalState>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const del = useMutation({
    mutationFn: (id: string) => rolesApi.deleteRole(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["roles", "list"] });
      navigate("/roles");
    },
  });

  const config: DetailPageConfig<Role> = {
    ...roleDetailConfig,
    actions: (record: Role) => {
      const acts = [
        {
          id: "edit",
          label: "Edit",
          variant: "secondary" as const,
          onClick: () => setModal({ kind: "edit", role: record }),
        },
        {
          id: "permissions",
          label: "Edit permissions",
          variant: "primary" as const,
          onClick: () => setModal({ kind: "permissions", role: record }),
        },
      ];
      if (!record.is_system) {
        acts.push({
          id: "delete",
          label: del.isPending ? "Deleting…" : "Delete",
          variant: "secondary" as const,
          onClick: () => {
            if (
              window.confirm(
                `Delete role "${record.display_name}"? This cannot be undone.`,
              )
            ) {
              del.mutate(record.id);
            }
          },
        });
      }
      return acts;
    },
    tabs: [
      {
        id: "overview",
        label: "Overview",
        render: (record: Role) => (
          <div
            style={{
              padding: 24,
              background: "var(--color-surface)",
              border: "1px solid var(--color-border)",
              borderRadius: "var(--radius-md, 8px)",
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 16,
            }}
          >
            <InfoRow label="Slug" value={record.slug} />
            <InfoRow
              label="Type"
              value={record.is_system ? "system" : "custom"}
            />
            <InfoRow
              label="Description"
              value={record.description || "—"}
            />
            <InfoRow label="Priority" value={record.priority} />
            <InfoRow label="Permissions" value={record.permission_count} />
            <InfoRow label="Users" value={record.user_count} />
            <InfoRow
              label="Active"
              value={record.is_active ? "Yes" : "No"}
            />
          </div>
        ),
      },
      {
        id: "permissions",
        label: "Permissions",
        render: (record: Role) => (
          <button
            type="button"
            data-action="open-permissions-from-tab"
            onClick={() => setModal({ kind: "permissions", role: record })}
            className="btn btn-secondary"
          >
            Open permission matrix ({record.permission_count} granted)
          </button>
        ),
      },
    ],
  };

  return (
    <>
      <DetailPage config={config} />
      {modal?.kind === "edit" ? (
        <RoleFormModal
          mode="edit"
          initial={modal.role}
          onClose={() => setModal(null)}
        />
      ) : null}
      {modal?.kind === "permissions" ? (
        <PermissionMatrix
          role={modal.role}
          onClose={() => setModal(null)}
        />
      ) : null}
    </>
  );
}
